import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Tooltip } from "@mui/material";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import Papa from "papaparse";
import { get, off, onValue, ref } from "firebase/database";
import { useParams } from "react-router-dom";

import { database } from "../../firebase";
import MetricCard from "../ui/MetricCard";
import RecordDialog from "../ui/RecordDialog";
import EmptyState from "../ui/EmptyState";
import useSnack from "../ui/useSnack";
import Chart from "../charts/Chart";
import { baseChartOption, lineSeries, seriesColor } from "../charts/chartTheme";
import { saveChartImage } from "../../utils/saveChartImage";
import { EM_DASH, describe, formatRelative, formatTimestamp, latestOf } from "../../utils/format";

const PROXY = "https://proxyserver-ten.vercel.app/proxy-csv?path=";

/* Three axes → the first three categorical slots, which validate all-pairs in
   both themes. Line type gives a second, colour-independent channel. */
const AXES = [
  { key: "x", label: "X", column: "Acceleration X", slot: 0, lineType: "solid" },
  { key: "y", label: "Y", column: " Acceleration Y", slot: 1, lineType: "dashed" },
  { key: "z", label: "Z", column: " Acceleration Z", slot: 2, lineType: "dotted" },
];

const storagePathFromUrl = (url) => {
  const matches = String(url || "").match(/\/o\/(.+)\?/);
  return matches?.[1] ? decodeURIComponent(matches[1]) : null;
};

const Accelerometer = ({ title = "Accelerometer", extraActions, menu }) => {
  const { watchId } = useParams();
  const chartRef = useRef(null);
  const { snack, showSnack } = useSnack();

  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusDescription, setStatusDescription] = useState("");
  const [trace, setTrace] = useState({ samples: [], x: [], y: [], z: [] });

  useEffect(() => {
    const watchRef = ref(database, `Health/${watchId}/accelerometer`);

    const handler = onValue(watchRef, (snapshot) => {
      if (!snapshot.exists()) {
        setRecords([]);
        setSelectedRecord(null);
        return;
      }

      const all = Object.entries(snapshot.val())
        .map(([ts, record]) => ({ ...record, timestamp: ts }))
        .filter((record) => record.url && record.timestamp)
        .sort((a, b) => Number(b.timestamp) - Number(a.timestamp));

      setRecords(all);
      setSelectedRecord((previous) => {
        if (!previous) return all[0] ?? null;
        return all.find((r) => r.timestamp === previous.timestamp) ?? all[0] ?? null;
      });
    });

    return () => off(watchRef, "value", handler);
  }, [watchId]);

  useEffect(() => {
    if (!selectedRecord?.timestamp) {
      setStatusDescription("");
      return;
    }

    get(
      ref(
        database,
        `Health/${watchId}/accelerometer/${selectedRecord.timestamp}/accelerometerStatusDescription`
      )
    )
      .then((snapshot) => setStatusDescription(snapshot.exists() ? snapshot.val() : ""))
      .catch((error) => {
        console.error("Error fetching accelerometerStatusDescription:", error);
        setStatusDescription("");
      });
  }, [selectedRecord, watchId]);

  const loadTrace = useCallback(async (url) => {
    const path = storagePathFromUrl(url);
    if (!path) return;

    setLoading(true);
    try {
      const response = await fetch(`${PROXY}${encodeURIComponent(path)}`);
      const csvText = await response.text();

      const parsed = Papa.parse(csvText, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
      });

      const column = (name) =>
        parsed.data.map((row) => {
          const value = parseFloat(row[name]);
          return Number.isFinite(value) ? Number(value.toFixed(2)) : null;
        });

      setTrace({
        samples: parsed.data.map((_, index) => index + 1),
        x: column(AXES[0].column),
        y: column(AXES[1].column),
        z: column(AXES[2].column),
      });
    } catch (error) {
      console.error("Error fetching or parsing accelerometer CSV:", error);
      setTrace({ samples: [], x: [], y: [], z: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRecord?.url) loadTrace(selectedRecord.url);
  }, [selectedRecord, loadTrace]);

  const stats = useMemo(
    () => describe([...trace.x, ...trace.y, ...trace.z], 2),
    [trace]
  );

  const accent = seriesColor(0);

  const buildOption = () =>
    baseChartOption({
      categories: trace.samples,
      series: AXES.map((axis) =>
        lineSeries({
          name: `${axis.label} axis`,
          data: trace[axis.key],
          color: seriesColor(axis.slot),
          lineType: axis.lineType,
          area: false,
          smooth: 0.2,
          width: 1.8,
          showSymbol: false,
        })
      ),
      showLegend: true,
      xName: "Sample",
      yName: "m/s²",
      tooltipFormatter: (params) => {
        if (!params?.length) return "";
        const rows = params
          .map(
            (p) =>
              `<div style="display:flex;gap:8px;justify-content:space-between">
                 <span>${p.marker} ${p.seriesName}</span>
                 <strong>${p.value ?? "—"}</strong>
               </div>`
          )
          .join("");
        return `Sample ${params[0].axisValue}${rows}`;
      },
    });

  const handleExport = async () => {
    const result = await saveChartImage(chartRef, watchId, "accelerometer");
    showSnack(
      result.ok
        ? "Chart saved — find it on the Files page."
        : "Could not export the chart image.",
      result.ok ? "success" : "error"
    );
  };

  return (
    <>
      <MetricCard
        accent={accent}
        icon={<ShowChartOutlinedIcon />}
        title={title}
        meta={
          <>
            <span className="wg-chip wg-chip--accent">
              {selectedRecord ? "Record loaded" : "No records"}
            </span>
            {selectedRecord && (
              <span className="wg-chip">
                {formatTimestamp(selectedRecord.timestamp)}
              </span>
            )}
          </>
        }
        menu={menu}
        actions={
          <>
            {extraActions}
            <Tooltip title="Download a PNG and archive it under Files">
              <span>
                <Button
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={handleExport}
                  disabled={!trace.samples.length}
                >
                  Export
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => setSelectedRecord(latestOf(records))}
              disabled={!records.length}
            >
              Latest
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDialogOpen(true)}
              disabled={!records.length}
            >
              Select record
            </Button>
          </>
        }
        note={statusDescription}
        /* Same four-tile shape as the other waveform panels:
           Samples / Amplitude / Average / Recorded. */
        stats={[
          {
            label: "Samples",
            value: trace.samples.length || EM_DASH,
            meta: "Points per axis",
          },
          { label: "Amplitude", value: stats.range, unit: "m/s²", meta: "Across all axes" },
          { label: "Average", value: stats.avg, unit: "m/s²", meta: "Across all axes" },
          {
            label: "Recorded",
            value: selectedRecord ? formatRelative(selectedRecord.timestamp) : EM_DASH,
            small: true,
            meta: selectedRecord?.displayName || "Unassigned",
          },
        ]}
        loading={loading}
        empty={
          !loading && !selectedRecord ? (
            <EmptyState
              icon={<ShowChartOutlinedIcon />}
              title="Load a motion trace"
              copy="Choose a stored capture to review the X, Y and Z acceleration vectors."
              action={
                <Button
                  variant="contained"
                  onClick={() => setDialogOpen(true)}
                  disabled={!records.length}
                >
                  Select a record
                </Button>
              }
            />
          ) : null
        }
      >
        <Chart
          buildOption={buildOption}
          chartRef={chartRef}
          height={300}
          deps={[trace]}
        />
      </MetricCard>

      <RecordDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(record) => {
          setSelectedRecord(record);
          setDialogOpen(false);
        }}
        records={records}
        accent={accent}
        title="Accelerometer captures"
        getTitle={(record) => record.displayName || "Motion capture"}
        getMeta={(record) =>
          `${formatTimestamp(record.timestamp)} · ${formatRelative(record.timestamp)}`
        }
        isSelected={(record) => selectedRecord?.timestamp === record.timestamp}
      />

      {snack}
    </>
  );
};

export default Accelerometer;
